/**
 * The player's data channel, parsed at the boundary. See docs/rules/api_boundary.md. A metadata
 * packet is parsed in a second step, against its own union.
 */

import type { ViolationOrigin, ViolationSink } from '@/types/contract';
import { ViolationSourceKind } from '@/types/contract';
import { MetadataPacketSchema } from '@/types/metadataPacket';
import {
    type DataChannelFrame, MetadataFrameSchema, PositionsFrameSchema, StreamStatusFrameSchema,
    SyncPointFrameSchema, VIDEO_MESSAGE_MAGIC, type VideoMessage, VideoMessageHeaderSchema,
    VideoTransportFrameSchema,
} from '@/types/webrtc';

import { getKeyedOption, parseFrameValue, parseKeyedFrame } from './contract';
import { contractReporter } from './contractReporter';
import { getSignallingEndpoint } from './playerSignalling';

const DATA_CHANNEL_OPTIONS = [
    getKeyedOption('rtpTimestamp', SyncPointFrameSchema),
    getKeyedOption('status', StreamStatusFrameSchema),
    getKeyedOption('positions', PositionsFrameSchema),
    getKeyedOption('metadata', MetadataFrameSchema),
    getKeyedOption('videoTransport', VideoTransportFrameSchema),
] as const;

/**
 * The byte layout of a video message header; see VideoMessageHeaderSchema. Version 1 ends before
 * the width.
 */
const VIDEO_MESSAGE_HEADER_SIZE_V1 = 44;
const VIDEO_MESSAGE_HEADER_SIZE = 48;
const MAGIC_OFFSET = 0;
const VERSION_OFFSET = 4;
const CODEC_OFFSET = 5;
const FLAGS_OFFSET = 8;
const DEVICE_ID_OFFSET = 12;
const DEVICE_ID_SIZE = 16;
const FRAME_INDEX_OFFSET = 28;
const RTP_TIMESTAMP_OFFSET = 32;
const TIMESTAMP_US_OFFSET = 36;
const WIDTH_OFFSET = 44;
const HEIGHT_OFFSET = 46;
/** The dash positions of a textual UUID, counted in hex digits. */
const UUID_GROUP_ENDS = [8, 12, 16, 20];
const HEX_RADIX = 16;
const HEX_DIGITS_PER_BYTE = 2;

function readUuid(bytes: Uint8Array): string {
    let hex = '';
    bytes.forEach((byte, index) => {
        hex += byte.toString(HEX_RADIX).padStart(HEX_DIGITS_PER_BYTE, '0');
        if (UUID_GROUP_ENDS.includes((index + 1) * HEX_DIGITS_PER_BYTE)) { hex += '-'; }
    });
    return hex;
}

/** The header fields as the bytes spell them, for the schema to judge; null when too short. */
function readVideoMessageHeader(buffer: ArrayBuffer): Record<string, unknown> | null {
    if (buffer.byteLength < VIDEO_MESSAGE_HEADER_SIZE_V1) { return null; }
    const view = new DataView(buffer);
    const version = view.getUint8(VERSION_OFFSET);
    const hasSize = version > 1;
    if (hasSize && buffer.byteLength < VIDEO_MESSAGE_HEADER_SIZE) { return null; }
    const magicBytes = new Uint8Array(buffer, MAGIC_OFFSET, VIDEO_MESSAGE_MAGIC.length);
    return {
        magic: String.fromCharCode(...magicBytes),
        version,
        codec: view.getUint8(CODEC_OFFSET),
        width: hasSize ? view.getUint16(WIDTH_OFFSET) : 0,
        height: hasSize ? view.getUint16(HEIGHT_OFFSET) : 0,
        flags: view.getUint32(FLAGS_OFFSET),
        deviceId: readUuid(new Uint8Array(buffer, DEVICE_ID_OFFSET, DEVICE_ID_SIZE)),
        frameIndex: view.getUint32(FRAME_INDEX_OFFSET),
        rtpTimestamp: view.getUint32(RTP_TIMESTAMP_OFFSET),
        timestampUs: Number(view.getBigInt64(TIMESTAMP_US_OFFSET)),
    };
}

/**
 * Reads the binary messages of the session's data channel: the video frames of a camera the
 * WebRTC path refuses (see VideoTransport). A message that is not one is a violation.
 */
export function createVideoMessageDecoder(
    url: string, serverId: string, sink: ViolationSink = contractReporter,
): (buffer: ArrayBuffer) => VideoMessage | null {
    const origin: ViolationOrigin = {
        endpoint: `${getSignallingEndpoint(url)} data channel video`,
        source: {
            kind: ViolationSourceKind.Relay,
            serverId,
        },
    };
    return (buffer: ArrayBuffer): VideoMessage | null => {
        const header = parseFrameValue(
            VideoMessageHeaderSchema, readVideoMessageHeader(buffer) ?? {}, origin, sink);
        if (header === null) { return null; }
        const headerSize =
            header.version === 1 ? VIDEO_MESSAGE_HEADER_SIZE_V1 : VIDEO_MESSAGE_HEADER_SIZE;
        return { ...header, data: new Uint8Array(buffer, headerSize) };
    };
}

/**
 * Reads the data channel of the session whose signalling socket is `url`. The channel has no
 * URL of its own, so it is named after that socket.
 */
export function createDataChannelDecoder(
    url: string, serverId: string, sink: ViolationSink = contractReporter,
): (text: string) => DataChannelFrame | null {
    const origin: ViolationOrigin = {
        endpoint: `${getSignallingEndpoint(url)} data channel`,
        source: {
            kind: ViolationSourceKind.Relay,
            serverId,
        },
    };
    return (text: string): DataChannelFrame | null => {
        const message = parseKeyedFrame(DATA_CHANNEL_OPTIONS, text, origin, sink);
        if (message === null) {
            return null;
        }
        switch (message.key) {
            case 'metadata': {
                const packet = parseFrameValue(
                    MetadataPacketSchema, message.frame.metadata, origin, sink);
                return packet === null
                    ? null
                    : {
                        key: 'metadata',
                        frame: packet,
                    };
            }
            default:
                return message;
        }
    };
}
